import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { CoBuyApi, FilterCoBuyOptions } from 'api';
import { coBuyQueryInit } from 'containers/SaleManager/Discount/CoBuy/commons/query';

export const formatCobuy = item => {
  // get statistic info
  const statisticInfo = { totalOrder: 0, totalPrice: 0 };

  if (item.orders) {
    statisticInfo.totalOrder = item.orders.length;
    statisticInfo.totalPrice = item.orders.reduce(
      (sum, a) => sum + +a.totalPrice,
      0
    );
  }
  return {
    ...item,
    key: item.id,
    value: item.id,
    duration: {
      startedAt: item.startedAt,
      finishedAt: item.finishedAt,
    },
    infoBox: {
      name: item.name,
      code: item.code,
      totalProduct: item.totalProduct,
      totalMember: item.totalMember,
      ...statisticInfo,
    },
  };
};

export const filterCoBuy = createAsyncThunk(
  'coBuy/filterCoBuy',
  async (option: FilterCoBuyOptions, { rejectWithValue }) => {
    try {
      const response = await CoBuyApi.filter(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const filterCoBuyWithCollaborator = createAsyncThunk(
  'coBuy/filterCoBuyWithCollaborator',
  async (option: FilterCoBuyOptions, { rejectWithValue }) => {
    try {
      const response = await CoBuyApi.filterWithCollaborator(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findReloadedCoBuyRowForPurchase = createAsyncThunk(
  // for reloading
  'coBuy/findReloadedCoBuyRowForPurchase',
  async (id: string, { rejectWithValue }) => {
    try {
      const query: any = { ...coBuyQueryInit };
      query.order = {
        [query.order.name]: query.order.value,
      };
      const response = await CoBuyApi.find({
        ...query,
        where: {
          id,
        },
      });

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findCoBuy = createAsyncThunk(
  'coBuy/findCoBuy',
  async (option: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await CoBuyApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createCoBuy = createAsyncThunk(
  'coBuy/createCoBuy',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await CoBuyApi.create(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneCoBuy = createAsyncThunk(
  'coBuy/findOneCoBuy',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, option } = params;
      const response = await CoBuyApi.findOne(id, option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateCoBuy = createAsyncThunk(
  'coBuy/updateCoBuy',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await CoBuyApi.update(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkDuplicateCoBuyCode = createAsyncThunk(
  'coBuy/checkDuplicateCoBuyCode',
  async (code: string, { rejectWithValue }) => {
    try {
      const response = await CoBuyApi.checkCoBuyCodeIsExisted(code);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const countCoBuy = createAsyncThunk(
  'coBuy/countCoBuy',
  async (option, { rejectWithValue }) => {
    try {
      const response = await CoBuyApi.getCount(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface IState {
  reloadFlag: boolean;
  tableList: Array<any>;
  myList: Array<any>;
  purchaseList: Array<any>;
  purchaseReloadedRow?: any;
  collaboratorList: Array<any>;
  list: Array<any>;
  savedPagination: any;
  savedList: Array<any>;
}

const initialState: IState = {
  reloadFlag: false,
  tableList: [],
  myList: [],
  purchaseList: [],
  purchaseReloadedRow: undefined,
  collaboratorList: [],
  list: [],
  savedPagination: { current: 1, next: 2, prev: 0, take: 10, total: 0 },
  savedList: [],
};

const coBuySlice = createSlice({
  name: 'coBuy',
  initialState,
  reducers: {
    reloadCoBuy(state) {
      state.reloadFlag = !state.reloadFlag;
    },
    updateSavedPaginationCobuy(state, action) {
      state.savedPagination = action.payload;
    },
    updateSavedListCobuy(state, action) {
      state.savedList = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(filterCoBuy.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          // get statistic info
          const statisticInfo = { totalOrder: 0, totalPrice: 0 };

          if (item.orders) {
            statisticInfo.totalOrder = item.orders.length;
            statisticInfo.totalPrice = item.orders.reduce(
              (sum, a) => sum + +a.totalPrice,
              0
            );
          }
          return {
            ...item,
            key: item.id,
            value: item.id,
            duration: {
              startedAt: item.startedAt,
              finishedAt: item.finishedAt,
            },
            infoBox: {
              name: item.name,
              code: item.code,
              totalProduct: item.totalProduct,
              totalMember: item.totalMember,
              ...statisticInfo,
            },
          };
        });

        state.myList = newData;
      })
      .addCase(findReloadedCoBuyRowForPurchase.fulfilled, (state, action) => {
        const { data } = action.payload;
        const item = data[0];
        const newData = {
          ...item,
          key: item.id,
          value: item.id,
          duration: {
            startedAt: item.startedAt,
            finishedAt: item.finishedAt, // NOTE: api has exclude finishedAt
          },
          infoBox: {
            name: item.name,
            code: item.code,
            totalProduct: item.totalProduct,
            totalMember: item.totalMember,
          },
        };

        state.purchaseReloadedRow = newData;
      })

      .addCase(filterCoBuyWithCollaborator.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
            duration: {
              startedAt: item.startedAt,
              finishedAt: item.finishedAt,
            },
            infoBox: {
              name: item.name,
              code: item.code,
              totalProduct: item.totalProduct,
              totalMember: item.totalMember,
            },
          };
        });

        state.collaboratorList = newData;
      })

      .addCase(findCoBuy.fulfilled, (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
          };
        });

        state.list = newData;
      });
  },
});

const { actions, reducer: coBuyReducer } = coBuySlice;
export const { reloadCoBuy, updateSavedPaginationCobuy, updateSavedListCobuy } =
  actions;

export default coBuyReducer;
